import type { AgenticRecommendation, Finding } from './types';

/**
 * isPersonaVoiced is false only when the selected persona has no override and is not the persona the
 * run was authored as, so the reader is looking at someone else's words. Showing that silently would
 * misrepresent whose framing it is.
 */
export type PersonaView =
  | { kind: 'withheld'; reason: string }
  | {
      kind: 'voiced';
      businessStatement: string;
      recommendations: AgenticRecommendation[];
      isPersonaVoiced: boolean;
    };

/**
 * Only businessStatement and recommendations ever vary by persona — personaOverrideSchema has no
 * field for anything else, which is what makes drift on the shared facts structurally impossible.
 */
export const resolvePersonaView = (
  finding: Finding,
  personaId: string,
  sharedCorePersonaId: string,
): PersonaView => {
  const withheldReason = finding.withheldFrom?.[personaId];
  if (withheldReason !== undefined) {
    return { kind: 'withheld', reason: withheldReason };
  }

  const core = {
    kind: 'voiced' as const,
    businessStatement: finding.businessStatement,
    recommendations: finding.recommendations,
  };

  if (personaId === sharedCorePersonaId) {
    return { ...core, isPersonaVoiced: true };
  }

  const override = finding.personaOverrides?.[personaId];
  if (override === undefined) {
    return { ...core, isPersonaVoiced: false };
  }

  return {
    kind: 'voiced',
    businessStatement: override.businessStatement,
    recommendations: override.recommendations,
    isPersonaVoiced: true,
  };
};

export const resolvePersonaViews = (
  findings: Finding[],
  personaId: string,
  sharedCorePersonaId: string,
): Map<string, PersonaView> =>
  new Map(
    findings.map((finding) => [
      finding.id,
      resolvePersonaView(finding, personaId, sharedCorePersonaId),
    ]),
  );

export const getPrimaryRecommendation = (
  recommendations: AgenticRecommendation[],
): AgenticRecommendation | undefined =>
  recommendations.find((recommendation) => recommendation.primary) ?? recommendations[0];
